import type { ConversationStyle } from '@aurora/shared-generated/types/graphql-schema-types';
import React, { useCallback, useContext, useState } from 'react';
import { Dropdown, useClassNameMapper } from 'react-bootstrap';
import type { UseDropdownMenuOptions } from 'react-overlays/DropdownMenu';
import { SharedComponent } from '../../../enums';
import { dropdownPopperConfig } from '../../../helpers/ui/PopperJsHelper';
import Icons from '../../../icons';
import AccessibleDropdownToggle from '../../common/AccessibleDropdownToggle/AccessibleDropdownToggle';
import type { ButtonVariant } from '../../common/Button/enums';
import { IconSize } from '../../common/Icon/enums';
import Icon from '../../common/Icon/Icon';
import AppContext from '../../context/AppContext/AppContext';
import NodeIcon from '../../nodes/NodeIcon/NodeIcon';
import NodeTitle from '../../nodes/NodeTitle/NodeTitle';
import useTranslation from '../../useTranslation';
import type { NodeSelectorDropdownItemDisabledProps } from '../NodeSelectorDropdownItem/NodeSelectorDropdownItem';
import NodeSelectorDropdownMenu from '../NodeSelectorDropdownMenu/NodeSelectorDropdownMenu';
import type { NodePickerNode } from '../types';
import localStyles from './NodeSelectorDropdown.module.pcss';

/**
 * Common props used by both the static and node dropdown toggles
 */
interface CommonNodeSelectorToggleProps {
  /**
   * Variant for the dropdown toggle
   */
  toggleVariant: ButtonVariant;
  /**
   * Classname(s) to apply to the dropdown toggle element
   */
  toggleClassName?: string;
}

/**
 * Props specific to the dropdown toggle the displays a node's icon, title, and optionally, a menu icon
 */
interface NodeToggleProps extends CommonNodeSelectorToggleProps {
  /**
   * The dropdown toggle type
   */
  type: 'node';
  /**
   * Whether to display the hamburger menu icon in the toggle
   */
  useMenuIcon: boolean;
}

/**
 * Props specific to the dropdown toggle that displays only static text
 */
interface StaticToggleProps extends CommonNodeSelectorToggleProps {
  /**
   * The dropdown toggle type
   */
  type: 'static';
  /**
   * Static text to render in the dropdown toggle
   */
  text: string;
}

export type NodeSelectorToggleProps = NodeToggleProps | StaticToggleProps;

interface Props {
  /**
   * Props used to render the node selector's dropdown toggle in one of two configurations (static or node)
   */
  toggleProps: NodeSelectorToggleProps;
  /**
   * Placeholder text for search box
   */
  searchPlaceholderText?: string;
  /**
   * Selected node on the dropdown.
   */
  selectedNode: NodePickerNode;
  /**
   * @callback
   * A callback function tp handle event when a node is selected.
   */
  onNodeSelect: (node: NodePickerNode) => void;
  /**
   * Classname(s) to apply to the dropdown div
   */
  className?: string;
  /**
   * Classname(s) to apply to the dropdown menu
   */
  menuClassName?: string;
  /**
   * The popper.js config used by the dropdown menu
   */
  menuPopperConfig?: UseDropdownMenuOptions['popperConfig'];
  /**
   * Whether to show empty nodes
   */
  hideEmptyNodes?: boolean;
  /**
   * Props used to disable and style dropdown items in the NodeSelectorDropdownMenu
   */
  disabledProps?: NodeSelectorDropdownItemDisabledProps;
  /**
   * The conversation styles of boards to be displayed in the menu along with other container nodes.
   * If specified, only boards of the specified conversation styles will be displayed along with container
   * nodes, else all nodes and boards are displayed
   */
  conversationStylesToBeIncluded?: Array<ConversationStyle>;
  /**
   * Whether hidden nodes should be displayed
   */
  showHiddenNodes?: boolean;
}

/**
 * Node Selector Dropdown
 *
 * @author Vishnu Das
 */
const NodeSelectorDropdown: React.FC<React.PropsWithChildren<Props>> = ({
  selectedNode,
  onNodeSelect,
  className,
  menuClassName,
  menuPopperConfig = dropdownPopperConfig,
  toggleProps,
  searchPlaceholderText,
  hideEmptyNodes = false,
  disabledProps,
  conversationStylesToBeIncluded,
  showHiddenNodes = false
}: Props) => {
  const cx = useClassNameMapper(localStyles);
  const { community } = useContext(AppContext);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const { formatMessage, loading: textLoading } = useTranslation(
    SharedComponent.NODE_SELECTOR_DROPDOWN
  );

  /**
   * Sets the selected node state when a selection is made on node picker dropdown
   * @param node selected node on dropdown menu
   */
  const handleNodeSelectorFilter = useCallback(
    (node: NodePickerNode) => {
      onNodeSelect(node);
    },
    [onNodeSelect]
  );

  if (textLoading) {
    return null;
  }

  /**
   * Renders icon for a given node
   */
  function renderNodeIcon(): React.ReactElement {
    return selectedNode?.id === community?.id ? (
      <Icon size={IconSize.PX_16} icon={Icons.HomeIcon} className={cx('lia-icon')} />
    ) : (
      <NodeIcon node={selectedNode} className={cx('lia-icon')} size={IconSize.PX_16} />
    );
  }

  /**
   * Renders title for a given node
   */
  function renderNodeTitle(): React.ReactElement {
    return <NodeTitle node={selectedNode} as="span" className={cx('lia-title lia-g-clamp')} />;
  }

  /**
   * Renders the static or node dropdown toggle
   */
  function renderDropdownToggle(): React.ReactElement {
    const { toggleVariant, toggleClassName } = toggleProps;
    if (toggleProps.type === 'static') {
      // static text dropdown toggle
      return (
        <Dropdown.Toggle
          as={AccessibleDropdownToggle}
          variant={toggleVariant}
          size="lg"
          className={cx(toggleClassName, 'lia-dropdown-toggle lia-g-dropdown-toggle')}
          data-testid="NodeSelectorToggle"
          aria-label={`${formatMessage('filterBy', { name: toggleProps.text })} ${formatMessage(
            'actionMenu.ariaLabel'
          )}`}
        >
          {toggleProps.text}
        </Dropdown.Toggle>
      );
    } else {
      // node dropdown toggle
      return (
        <Dropdown.Toggle
          as={AccessibleDropdownToggle}
          variant={toggleVariant}
          size="lg"
          className={cx(toggleClassName, 'lia-dropdown-toggle lia-g-dropdown-toggle', {
            'lia-g-no-arrow': toggleProps.useMenuIcon
          })}
          data-testid="NodeSelectorToggle"
          aria-label={`${selectedNode.title} ${formatMessage('actionMenu.ariaLabel')}`}
        >
          {toggleProps.useMenuIcon && (
            <Icon size={IconSize.PX_16} icon={Icons.MenuIcon} className={cx('lia-icon-menu')} />
          )}
          {renderNodeIcon()}
          {renderNodeTitle()}
        </Dropdown.Toggle>
      );
    }
  }

  return (
    <Dropdown
      className={cx('lia-node-picker', className)}
      data-testid="NodeSelectorDropdown"
      onToggle={isOpen => {
        setDropdownOpen(isOpen);
      }}
      data-menu-open={dropdownOpen}
    >
      {renderDropdownToggle()}

      <NodeSelectorDropdownMenu
        selectedNode={selectedNode}
        onNodeSelect={handleNodeSelectorFilter}
        dropdownOpen={dropdownOpen}
        searchPlaceholderText={searchPlaceholderText}
        hideEmptyNodes={hideEmptyNodes}
        disabledProps={disabledProps}
        className={menuClassName}
        conversationStylesToBeIncluded={conversationStylesToBeIncluded}
        popperConfig={menuPopperConfig}
        showHiddenNodes={showHiddenNodes}
      />
    </Dropdown>
  );
};

export default NodeSelectorDropdown;
